import type {
  CalendarDevice,
  DeviceWithSlots,
  ParticipantResponse,
} from '@/types/scheduling-api';
import type { DayListRow } from '@/scheduling/lib/calendar-mapping';

/**
 * Devices tab column model (PROD-2007). Pure helpers so the ordering, header
 * copy and grouping rules are unit-tested independently of the grid.
 *
 * Rulings (Andrew, 12 Sep 2026): one column per device of the selected
 * practice, ordered by subtype then name. Header line 2 WAS `subtype · room ·
 * technician` ("mobile" when no room) and is `room · technician` since
 * 2026-09-14 — `deviceHeaderLine` carries why both halves left.
 */

/**
 * "No technician assigned" — the device strip card's second line, the
 * Book-a-device picker card's meta line and, since 2026-09-14, the calendar
 * column header's second line.
 *
 * It lives HERE rather than in `calendar-mapping` (which re-exports it, so the
 * call sites that already read it from there do not have to move) because the
 * mapper imports this module and not the other way round. Without the move
 * `deviceHeaderLine` could not share the constant, and a second copy of the
 * string is exactly the drift `technicianLine` was extracted to stop.
 *
 * `MOBILE_DEVICE_LABEL` used to sit here. It was read in one place — the header
 * line's no-room fallback — and that fallback is gone, so the constant went
 * with it rather than staying as vocabulary nothing speaks.
 */
export const NO_TECHNICIAN = 'No technician assigned';

/** `referenceType` the backend stamps on a device participant (S12). */
const DEVICE_REFERENCE_TYPE = 'device';

function norm(s: string | null | undefined): string {
  return (s ?? '').trim();
}

function cmp(a: string, b: string): number {
  return a.toLowerCase().localeCompare(b.toLowerCase());
}

/** Only live devices get a column — archived ones are history. */
export function activeDevices(devices: readonly CalendarDevice[]): CalendarDevice[] {
  return devices.filter((d) => d.status === 'active');
}

/**
 * Subtype A→Z (case-insensitive), then name A→Z. Devices with no subtype sort
 * after every typed device so the typed groups read first.
 */
export function orderDevices(devices: readonly CalendarDevice[]): CalendarDevice[] {
  return [...devices].sort((a, b) => {
    const sa = norm(a.subtype);
    const sb = norm(b.subtype);
    if (sa !== sb) {
      if (!sa) return 1;
      if (!sb) return -1;
      const c = cmp(sa, sb);
      if (c !== 0) return c;
    }
    return cmp(norm(a.name), norm(b.name));
  });
}

/**
 * Distinct subtype values of the practice's devices, for the in-tab filter
 * (chambers, chairs, imaging…). Case-insensitive de-dupe, first spelling wins.
 * Devices with no subtype contribute nothing.
 *
 * Ordered by how many devices carry the subtype (descending), then A→Z.
 * `subtype` is free text on the device registry — dev has values like
 * "Tier B - Room" — so an alphabetical list buries the subtypes a receptionist
 * actually filters by behind one-off spellings. Frequency puts the real
 * groupings first, which is also what makes capping the row safe (review #4).
 */
export function deviceSubtypes(devices: readonly CalendarDevice[]): string[] {
  const seen = new Map<string, { label: string; count: number }>();
  for (const d of devices) {
    const s = norm(d.subtype);
    if (!s) continue;
    const key = s.toLowerCase();
    const hit = seen.get(key);
    if (hit) hit.count += 1;
    else seen.set(key, { label: s, count: 1 });
  }
  return [...seen.values()]
    .sort((a, b) => (b.count - a.count) || cmp(a.label, b.label))
    .map((e) => e.label);
}

/** How many subtype chips the filter row shows before it offers "more". */
export const SUBTYPE_CHIP_LIMIT = 8;

/**
 * The chips to render, and how many are held back (review #4: the row was
 * unbounded free text). The first `limit` by the ordering above, UNION every
 * selected subtype — a selection must stay visible or it could not be
 * switched off — in the list's own order, so chips never jump around.
 */
export function capSubtypes(
  subtypes: readonly string[],
  selected: readonly string[],
  limit: number = SUBTYPE_CHIP_LIMIT,
): { visible: string[]; hiddenCount: number } {
  if (subtypes.length <= limit) return { visible: [...subtypes], hiddenCount: 0 };
  const isSelected = new Set(selected.map((s) => norm(s).toLowerCase()));
  const visible = subtypes.filter(
    (s, i) => i < limit || isSelected.has(norm(s).toLowerCase()),
  );
  return { visible, hiddenCount: subtypes.length - visible.length };
}

/**
 * Is the subtype chip row rendered at all?
 *
 * OFF since 2026-09-13 (Andrew: "remove for now or hide only the filter of the
 * categories of the devices and keep them under all"). Dev has 57 devices
 * spread over free-text subtypes, of which only a handful are bookable at all,
 * so the row was mostly one-device categories nobody filters by — and hiding it
 * IS "keep them under All": with no selection every bookable device shows.
 *
 * A HIDE, not a delete: `deviceSubtypes` / `capSubtypes` /
 * `filterDevicesBySubtype` and their tests all stay, and the row comes back by
 * flipping this one constant once enough devices are configured to group. The
 * page reads it too, so a subtype persisted in the store from before the hide
 * cannot keep filtering columns behind a control nobody can see.
 */
export const DEVICE_SUBTYPE_FILTER_ENABLED = false;

/** Empty selection = every device (same "All" semantic as the specialty chips). */
export function filterDevicesBySubtype(
  devices: readonly CalendarDevice[],
  selected: readonly string[],
): CalendarDevice[] {
  if (selected.length === 0) return [...devices];
  const wanted = new Set(selected.map((s) => norm(s).toLowerCase()));
  return devices.filter((d) => wanted.has(norm(d.subtype).toLowerCase()));
}

/**
 * Can this device actually be booked?
 *
 * BOTH halves of a device's schedule have to exist before a booking can be
 * made against it: a working window to book INSIDE (`workingHours`, the
 * device's active Work profile) and at least one product to book (
 * `appointmentProfile`, which is what decides the booking's length, colour and
 * price). With either missing the rail can render the device but never produce
 * a request — the window list resolves empty and the product chips resolve
 * empty — so the row is pure clutter with a "Not bookable" marker on it.
 *
 * Andrew, 2026-09-13: "show only the devices with working hours and
 * appointment profiles". Dev's demo practice has 57 active devices and 5 that
 * satisfy this, so the tab was 52 rows of noise around the 5 real columns.
 *
 * THE one place the rule is written. The calendar strip, the day-grid columns
 * and the Book-a-device picker all narrow through this predicate (via
 * `filterBookableDevices` for the registry-shaped list), so they cannot drift
 * into offering a device one surface knows is unbookable.
 *
 * WRITTEN AS `isBlockableDevice` PLUS THE PRODUCT (PROD-2454, bot review).
 * Bookable ⊃ blockable is an invariant, not a coincidence — everything you can
 * book you can also take out of service — and spelling the working-hours half
 * out twice would let the two drift apart silently under a later edit, with a
 * unit test as the only net. Composed, the superset holds by construction: a
 * change to what "has a usable window" means reaches BOTH predicates, and the
 * only thing this one adds is the thing a booking genuinely needs extra.
 */
export function isBookableDevice(
  device: Pick<DeviceWithSlots, 'workingHours' | 'appointmentProfile'> | null | undefined,
): boolean {
  return isBlockableDevice(device) && (device?.appointmentProfile?.length ?? 0) > 0;
}

/**
 * Can this device be BLOCKED? (PROD-2454.)
 *
 * Deliberately a WEAKER rule than `isBookableDevice`, and this is the one
 * place that difference is written down. Blocking asks a machine for a window
 * to mark busy; it never asks it for a product. A device with working hours
 * and no product cannot be booked — there is nothing to book — but can
 * perfectly well be taken out of service for a morning, so
 * `appointmentProfile` gets no say here.
 *
 * Working hours DO gate it, for two reasons that agree:
 *   - the break form bounds start/end to the actor's own windows
 *     (`useActorShifts`), so a device with none offers no pickable time and
 *     the panel could never be completed; and
 *   - those working hours are what PracticeManagement's
 *     `ActorScheduleProvisioner` writes the device's Scheduling schedule row
 *     from, and that row is what `BlockSlotCommand` resolves against once
 *     PROD-2451 lands. No hours, no schedule row, no block.
 *
 * A picker offering such a device would be a control that cannot work, which
 * is the one thing this screen does not do.
 *
 * This is also the BASE of `isBookableDevice`, which is written as this plus
 * the product. Widening or narrowing what counts as a usable window here
 * therefore moves both predicates together, and bookable ⊃ blockable stays
 * true by construction rather than by two definitions agreeing.
 */
export function isBlockableDevice(
  device: Pick<DeviceWithSlots, 'workingHours'> | null | undefined,
): boolean {
  return (device?.workingHours?.length ?? 0) > 0;
}

/**
 * Narrow the REGISTRY list to the BLOCKABLE devices — the sibling of
 * `filterBookableDevices` over `isBlockableDevice`, sharing its oracle
 * (`DeviceCalendarSlots` is the only feed carrying the schedule) and its
 * absent-feed rule: no feed yet yields an empty list, never the unfiltered
 * one, so the block picker holds its loading state instead of flashing every
 * device in the registry and dropping most of them a beat later.
 */
export function filterBlockableDevices(
  devices: readonly CalendarDevice[],
  feed: readonly DeviceWithSlots[] | null | undefined,
): CalendarDevice[] {
  const blockable = new Set((feed ?? []).filter(isBlockableDevice).map((d) => d.id));
  return devices.filter((d) => blockable.has(d.id));
}

/**
 * Narrow the REGISTRY list (`GET /api/Resource` → `CalendarDevice`) to the
 * bookable devices, using the card feed as the oracle.
 *
 * The two shapes exist for good reasons and neither is going away: the registry
 * is what owns identity, subtype, room and technician (and is what the columns
 * and the picker are built from), while only `DeviceCalendarSlots` carries the
 * schedule that decides bookability. So the id set comes from the feed and is
 * applied to the registry rather than duplicating the predicate per shape.
 *
 * An ABSENT feed (still loading, or the query is disabled off the Devices tab)
 * yields an empty list, not the unfiltered one: showing 57 rows and then
 * dropping 52 of them a moment later is worse than the caller holding its
 * loading state until the feed lands, which is what SchedulingPage does.
 */
export function filterBookableDevices(
  devices: readonly CalendarDevice[],
  feed: readonly DeviceWithSlots[] | null | undefined,
): CalendarDevice[] {
  const bookable = new Set((feed ?? []).filter(isBookableDevice).map((d) => d.id));
  return devices.filter((d) => bookable.has(d.id));
}

/**
 * The number the Devices tab badge shows — or `undefined` for NO BADGE
 * (PROD-2364).
 *
 * The badge exists to tell an operator whether the other tab is worth opening.
 * Both device feeds are gated on the tab being active (see `SchedulingPage`),
 * so until they have landed `filterBookableDevices` yields an empty list —
 * which is the ABSENCE of an answer, not the answer zero. Rendering `0` from
 * it actively tells a practice that HAS five bookable devices it has none,
 * which is strictly worse than rendering nothing: a missing badge invites the
 * click, a false zero forbids it.
 *
 * So the count is reported only once both feeds have resolved. A practice with
 * genuinely no bookable devices still reads `0` — the honest zero survives,
 * because `loaded` is what separates the two cases, not the length.
 *
 * Fetching the feeds on the Practitioners tab instead was rejected: the tab
 * gate is deliberate (`/api/Resource` is walked page by page and the slots POST
 * sits on the hottest calendar path), and this repo's convention is that device
 * queries fire only on the Devices tab.
 */
export function deviceTabBadgeCount(
  loaded: boolean,
  bookableDevices: readonly unknown[],
): number | undefined {
  return loaded ? bookableDevices.length : undefined;
}

/**
 * Free-text device search (PROD-2193). Andrew, 2026-09-13: "the device
 * dropdown should also be searchable" — this practice has 57 devices, and a
 * native `<select>` offers no way to find one.
 *
 * Matches the fields that actually tell one machine from another: `name`,
 * `subtype`, `technicianName` and `roomName`. Words are ANDed, so a second
 * word NARROWS ("chamber rami") instead of widening the list, and each word
 * is a case-insensitive substring so "sauna" finds "Infrared sauna". The
 * caller's ordering survives untouched.
 */
export function filterDevicesByQuery(
  devices: readonly CalendarDevice[],
  query: string,
): CalendarDevice[] {
  const words = norm(query).toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [...devices];
  return devices.filter((d) => {
    const haystack = [d.name, d.subtype, d.technicianName, d.roomName]
      .map((v) => norm(v).toLowerCase())
      .filter(Boolean)
      .join(' ');
    return words.every((w) => haystack.includes(w));
  });
}

/**
 * Column header second line: `Room 7 · Technician Rami`.
 *
 * Still written the way the rest of the app writes it — the device registry's
 * own row detail (`settings/components/resources/resource-details.ts`) reads
 * "Technician Rami", so the calendar does too (review #5; "tech:" / "no tech"
 * was drift).
 *
 * The SUBTYPE half left on 2026-09-14 (Andrew, on the live tab), and the room's
 * "mobile" fallback left with it. Two reasons, and the second is why the
 * fallback could not stay:
 *
 *   1. Subtype is a practitioner-facing classification of the machine, not
 *      something a receptionist books against — the same judgement that hid the
 *      subtype CHIP row (`DEVICE_SUBTYPE_FILTER_ENABLED`). Leading every column
 *      with it made the header open on the one field just ruled off the tab.
 *   2. It was redundant with the fallback. Dev's subtypes read "Tier A -
 *      Mobile" / "Tier C diagnostic - Mobile", and a device with no room reads
 *      "mobile", so every column stated the mobility twice: "Tier A - Mobile ·
 *      mobile · Technician Yousef Al Hammadi". With the subtype gone a bare
 *      "mobile" says nothing at all, so the room segment now appears only when
 *      the registry actually names a room.
 *
 * The technician is the half Andrew kept, which makes it the half that must
 * never be blank: `NO_TECHNICIAN` fills it, the same string the strip card and
 * the Book-a-device picker card already show. That is also what keeps the join
 * safe — the last segment is always non-empty, so the line can never collapse
 * to an empty string or to a dangling " · ". (The old line was immune to that
 * by accident, through two fallbacks that have both now left.)
 */
export function deviceHeaderLine(device: CalendarDevice): string {
  const room = norm(device.roomName);
  const tech = norm(device.technicianName);
  return [room, tech ? `Technician ${tech}` : NO_TECHNICIAN]
    .filter((part) => part.length > 0)
    .join(' · ');
}

/**
 * The device a booking occupies — its participant carries
 * `referenceType: "Device"` with the device id in `reference` (and no
 * `provider` / `patient` object).
 */
export function deviceIdFromParticipants(
  participants: readonly ParticipantResponse[] | null | undefined,
): string | undefined {
  if (!participants) return undefined;
  const hit = participants.find(
    (p) => (p.referenceType ?? '').toLowerCase() === DEVICE_REFERENCE_TYPE,
  );
  return hit?.reference || undefined;
}

/**
 * Bucket rows by `deviceId`, sorted by start. Rows with no device are dropped
 * — a device column can only show device bookings (and a practitioner row
 * reaching this grid would be a mapper regression, warned in dev).
 */
export function groupRowsByDevice(rows: readonly DayListRow[]): Map<string, DayListRow[]> {
  const out = new Map<string, DayListRow[]>();
  let dropped = 0;
  for (const row of rows) {
    const id = row.deviceId;
    if (!id) {
      dropped++;
      continue;
    }
    const list = out.get(id) ?? [];
    list.push(row);
    out.set(id, list);
  }
  if (dropped > 0 && import.meta.env.DEV) {
    console.warn(
      `[ByDeviceCalendar] groupRowsByDevice dropped ${dropped} row(s) with no deviceId — check the mapper.`,
    );
  }
  for (const list of out.values()) {
    list.sort((a, b) => a.startISO.localeCompare(b.startISO));
  }
  return out;
}
